import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Coins, Sparkles, Wallet } from 'lucide-react';
import {
  euro, KI_GUTHABEN_ART,
  type KiGuthabenAufladenEingabe, type KiGuthabenBuchung, type KiGuthabenBuchungSeite, type KiGuthabenStand,
} from '@dxl/shared';
import { Abzeichen, Button, DatenTabelle, Dialog, Feld, Hinweis, Karte, Skelett, toast, type Spalte } from '@dxl/ui';
import { api, ApiFehler } from '../lib/api';
import '../styles/ki-guthaben.css';

/**
 * KI-Guthaben des Marktplatzes (Vorkasse für die KI von DXL – getrennt vom Werbe-Guthaben):
 * Stand, Aufladen über Stripe Checkout (Credit-Pakete oder freier Betrag ab 10 €), Buchungen.
 */

/** Cent mit Bruchteilen (z. B. 0,66 Cent) als Euro */
export const euroGenau = (cent: number) =>
  (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: Math.abs(cent) < 100 && cent % 1 !== 0 ? 4 : 2 });

export const KI_GUTHABEN_SCHLUESSEL = ['konto', 'ki-guthaben'] as const;

export function useKiGuthaben(aktiv = true) {
  return useQuery({ queryKey: KI_GUTHABEN_SCHLUESSEL, queryFn: ({ signal }) => api<KiGuthabenStand>('/api/konto/guthaben', { signal }), enabled: aktiv });
}

/** Aufladen-Dialog: Credit-Pakete von DXL oder freier Betrag; weiter zu Stripe Checkout */
export function AufladenDialog({ offen, stand, onSchliessen }: { offen: boolean; stand: KiGuthabenStand | undefined; onSchliessen: () => void }) {
  const [wahl, setWahl] = useState<string>('');
  const [betrag, setBetrag] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    if (!offen) return;
    setWahl(stand?.pakete[0]?.id ?? 'frei');
    setBetrag('');
    setFehler(null);
  }, [offen, stand?.pakete]);
  const mindest = stand?.mindestCent ?? 1000;
  const freiCent = Math.round(Number(betrag.replace(/\./g, '').replace(',', '.')) * 100);
  const freiGueltig = Number.isFinite(freiCent) && freiCent >= mindest && freiCent <= 1_000_000;
  const aufladen = useMutation({
    mutationFn: (d: KiGuthabenAufladenEingabe) => api<{ url: string }>('/api/konto/guthaben/aufladen', { body: d }),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => setFehler(e instanceof ApiFehler ? (Object.values(e.felder)[0] ?? e.message) : 'Das Aufladen ist fehlgeschlagen.'),
  });
  const los = () => (wahl === 'frei' ? freiGueltig && aufladen.mutate({ betragCent: freiCent }) : aufladen.mutate({ paketId: wahl }));

  return (
    <Dialog
      offen={offen}
      gross
      breite={560}
      titel={<span className="dialog-titel"><span><Wallet aria-hidden /> KI-Guthaben aufladen</span></span>}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <Button art="sekundaer" onClick={onSchliessen}>Abbrechen</Button>
          <Button laedt={aufladen.isPending} disabled={!stand?.aufladbar || (wahl === 'frei' && !freiGueltig)} onClick={los}>Weiter zur Zahlung</Button>
        </>
      }
    >
      <div className="abschnitt kiguthaben-aufladen">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        {!stand?.aufladbar && <Hinweis art="warnung">Das Aufladen über Stripe ist noch nicht eingerichtet. Bitte wenden Sie sich an DXL.</Hinweis>}
        <p className="gedaempft">Das KI-Guthaben bezahlt die KI von DXL im Voraus (Anreicherung, Feld-KI, Kategorien, Texte). Es ist getrennt vom Werbe-Guthaben für Retail Media.</p>
        <fieldset className="kiguthaben-pakete">
          <legend className="sr-only">Betrag wählen</legend>
          {stand?.pakete.map((p) => (
            <label key={p.id} className={`kiguthaben-paket${wahl === p.id ? ' kiguthaben-paket--aktiv' : ''}`}>
              <input type="radio" name="ki-guthaben" checked={wahl === p.id} onChange={() => setWahl(p.id)} />
              <span className="kiguthaben-paket__betrag">{euro(p.betragCent)}</span>
              {p.bonusCent > 0 && <Abzeichen ton="akzent">+ {euro(p.bonusCent)} Bonus</Abzeichen>}
            </label>
          ))}
          <label className={`kiguthaben-paket kiguthaben-paket--frei${wahl === 'frei' ? ' kiguthaben-paket--aktiv' : ''}`}>
            <input type="radio" name="ki-guthaben" checked={wahl === 'frei'} onChange={() => setWahl('frei')} />
            <span className="kiguthaben-paket__betrag">Freier Betrag</span>
          </label>
        </fieldset>
        {wahl === 'frei' && (
          <Feld
            label="Betrag (Euro, netto)"
            inputMode="decimal"
            autoFocus
            value={betrag}
            onChange={(e) => setBetrag(e.target.value)}
            placeholder="z. B. 30"
            fehler={betrag && !freiGueltig ? `Bitte mindestens ${euro(mindest)} und höchstens 10.000 € angeben.` : undefined}
            hilfe={`Mindestens ${euro(mindest)}.`}
          />
        )}
        <p className="buchung__hinweis">
          <Coins aria-hidden />
          Alle Beträge netto zzgl. USt. Die Zahlung läuft über Stripe; das Guthaben steht bereit, sobald Stripe die Zahlung bestätigt (bei SEPA nach einigen Tagen).
        </p>
      </div>
    </Dialog>
  );
}

const spalten: Spalte<KiGuthabenBuchung>[] = [
  { schluessel: 'zeit', titel: 'Zeit', breite: '150px', zelle: (b) => <span className="gedaempft">{new Date(b.zeit).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span> },
  { schluessel: 'art', titel: 'Art', breite: '130px', zelle: (b) => <Abzeichen ton={b.art === 'aufladung' ? 'gut' : b.art === 'korrektur' ? 'akzent' : 'neutral'}>{KI_GUTHABEN_ART[b.art]}</Abzeichen> },
  { schluessel: 'text', titel: 'Beschreibung', breite: 'minmax(160px, 3fr)', nebensache: true, zelle: (b) => <span className="zelle-text">{b.text}</span> },
  { schluessel: 'betrag', titel: 'Betrag', breite: '110px', rechts: true, zelle: (b) => <span className={`betrag ${b.betragCent < 0 ? 'betrag--minus' : ''}`}>{euroGenau(b.betragCent)}</span> },
  { schluessel: 'stand', titel: 'Verfügbar', breite: '110px', rechts: true, nebensache: true, zelle: (b) => <span className="betrag gedaempft">{euroGenau(b.standCent)}</span> },
];

/** Buchungen mit Cursor-Paginierung – im Konto bzw. für DXL je Marktplatz */
export function KiGuthabenBuchungen({ pfad, hoehe = '420px' }: { pfad: string; hoehe?: string }) {
  const liste = useInfiniteQuery({
    queryKey: ['ki-guthaben-buchungen', pfad],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => api<KiGuthabenBuchungSeite>(`${pfad}?limit=100${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`, { signal }),
    getNextPageParam: (s) => s.weiter,
    placeholderData: keepPreviousData,
  });
  const { fetchNextPage } = liste;
  const mehrLaden = useCallback(() => void fetchNextPage(), [fetchNextPage]);
  if (liste.isError) return <Hinweis art="fehler">{(liste.error as Error).message}</Hinweis>;
  if (liste.isPending) return <div className="kiguthaben-skelett">{[1, 2, 3].map((i) => <Skelett key={i} />)}</div>;
  return (
    <DatenTabelle
      beschriftung="Buchungen des KI-Guthabens"
      spalten={spalten}
      zeilen={liste.data.pages.flatMap((s) => s.eintraege)}
      zeilenId={(b) => b.id}
      zeilenHoehe={44}
      hoehe={hoehe}
      hatMehr={liste.hasNextPage}
      laedtMehr={liste.isFetchingNextPage}
      mehrLaden={mehrLaden}
      leer={<div className="leer"><p className="leer__titel">Noch keine Buchungen</p><p>Aufladungen, Reservierungen und der Verbrauch der KI von DXL erscheinen hier.</p></div>}
    />
  );
}

/** Karte mit dem Stand (Paketseite) */
export function KiGuthabenKarte({ aufladenOffen = false }: { aufladenOffen?: boolean }) {
  const q = useKiGuthaben();
  const [offen, setOffen] = useState(aufladenOffen);
  const [buchungen, setBuchungen] = useState(false);
  useEffect(() => setOffen(aufladenOffen), [aufladenOffen]);
  const s = q.data;
  return (
    <section className="kiguthaben" aria-labelledby="kiguthaben-titel">
      <div className="kiguthaben__kopf">
        <h2 id="kiguthaben-titel"><Sparkles aria-hidden /> KI-Guthaben</h2>
        <span className="gedaempft">Vorkasse für die KI von DXL – getrennt vom Werbe-Guthaben</span>
      </div>
      <Karte className="kiguthaben__karte">
        <div className="kiguthaben__stand">
          <span className="paketstand__titel">Verfügbar</span>
          <span className={`kiguthaben__betrag${s?.niedrig && s.pflicht ? ' kiguthaben__betrag--niedrig' : ''}`}>{s ? euroGenau(s.verfuegbarCent) : <Skelett breite={120} />}</span>
          <span className="paketstand__zusatz">
            {s && s.reserviertCent > 0 ? `${euroGenau(s.reserviertCent)} für laufende Anfragen reserviert` : s?.pflicht ? 'Jede KI-Anfrage wird vorab reserviert und nach Verbrauch abgerechnet (× 1,1 auf den Einkauf).' : s?.dxl ? 'Für die KI von DXL.' : 'Nur nötig, wenn Sie die KI von DXL nutzen.'}
          </span>
        </div>
        <div className="kiguthaben__aktionen">
          {s?.darfAufladen && <Button onClick={() => setOffen(true)} disabled={!s.aufladbar}><Wallet aria-hidden /> Aufladen</Button>}
          <Button art="sekundaer" onClick={() => setBuchungen(true)}>Buchungen</Button>
        </div>
      </Karte>
      {s?.pflicht && s.niedrig && (
        <Hinweis art="warnung">Ihr KI-Guthaben ist niedrig. Ohne Guthaben werden KI-Anfragen nicht gesendet – laufende Anreicherungen pausieren, bis Sie aufladen.</Hinweis>
      )}
      {s && !s.dxl && <p className="gedaempft kiguthaben__fuss">Sie nutzen einen eigenen KI-Schlüssel oder keine KI. Die Quelle wählen Sie unter <Link to="/einstellungen" search={{ bereich: 'ki' }}>Einstellungen → KI</Link>.</p>}
      <AufladenDialog offen={offen} stand={s} onSchliessen={() => setOffen(false)} />
      <Dialog offen={buchungen} gross breite={880} titel="Buchungen des KI-Guthabens" onSchliessen={() => setBuchungen(false)} fuss={<Button art="sekundaer" onClick={() => setBuchungen(false)}>Schließen</Button>}>
        {buchungen && <KiGuthabenBuchungen pfad="/api/konto/guthaben/buchungen" />}
      </Dialog>
    </section>
  );
}

/* ---------------------------------------------------------------- guthaben_fehlt: Dialog überall */

export const GUTHABEN_FEHLT_EREIGNIS = 'dxl:ki-guthaben-fehlt';

/** Hört auf guthaben_fehlt aus lib/api.ts und zeigt den Weg zum Aufladen (Anreicherung, Feld-KI, Kategorien …) */
export function GuthabenFehltDialog() {
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    const zeigen = () => setOffen(true);
    window.addEventListener(GUTHABEN_FEHLT_EREIGNIS, zeigen);
    return () => window.removeEventListener(GUTHABEN_FEHLT_EREIGNIS, zeigen);
  }, []);
  return (
    <Dialog
      offen={offen}
      breite={480}
      titel={<span className="dialog-titel"><span><Wallet aria-hidden /> KI-Guthaben reicht nicht</span></span>}
      onSchliessen={() => setOffen(false)}
      fuss={
        <>
          <Button art="sekundaer" onClick={() => setOffen(false)}>Später</Button>
          <Link to="/paket" search={{ aufladen: true }} className="dxl-btn dxl-btn--primaer" onClick={() => setOffen(false)}>Guthaben aufladen</Link>
        </>
      }
    >
      <p>Ihr KI-Guthaben reicht für diese Anfrage nicht aus. Die Anfrage wurde nicht gesendet – es sind keine Kosten entstanden.</p>
      <p className="gedaempft">Nach dem Aufladen können Sie es sofort erneut versuchen; laufende Anreicherungen setzen von selbst fort.</p>
    </Dialog>
  );
}

/** Hinweis für Toasts: guthaben_fehlt wird schon im Dialog erklärt */
export function istGuthabenFehlt(e: unknown): boolean {
  return e instanceof ApiFehler && e.code === 'guthaben_fehlt';
}

export function toastAusserGuthaben(e: unknown, sonst: string) {
  if (!istGuthabenFehlt(e)) toast(e instanceof ApiFehler ? e.message : sonst, 'fehler');
}
